import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select,
  DatePicker,
  message, 
  Typography, 
  Space, 
  Popconfirm,
  Tag,
  Row,
  Col,
  Divider,
  Grid
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { companiesService, employeesService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import ResponsiveTable from '../../components/Common/ResponsiveTable';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;
const { useBreakpoint } = Grid;

const UserEmployees = () => {
  const { isReadOnly } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);
  const topPaginationRef = React.useRef(null);
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  const fetchCompanies = async () => {
    try {
      const resp = await companiesService.getCompanies();
      setCompanies(resp?.data?.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const fetchEmployees = async (search = '') => {
    setLoading(true);
    try {
      const data = await employeesService.getEmployees(search);
      setEmployees(data.data || []);
    } catch (error) {
      message.error('Error al cargar empleados');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchCompanies();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const showModal = (employee = null) => {
    setEditingEmployee(employee);
    setModalVisible(true);
    if (employee) {
      const normalized = {
        ...employee,
        fecha_inicio_rel_laboral: employee.fecha_inicio_rel_laboral
          ? dayjs(employee.fecha_inicio_rel_laboral)
          : null,
      };
      form.setFieldsValue(normalized);
    } else {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingEmployee(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const payload = { ...values };

      // Forzar Uso del CFDI a CN01
      payload.use_tax = 'CN01';

      // Normalizar fecha y calcular antigüedad como PnYnMnD (meses omitidos si son 0)
      if (values.fecha_inicio_rel_laboral) {
        const startStr = typeof values.fecha_inicio_rel_laboral === 'string'
          ? values.fecha_inicio_rel_laboral
          : values.fecha_inicio_rel_laboral.format('YYYY-MM-DD');

        payload.fecha_inicio_rel_laboral = startStr;

        const start = dayjs(startStr);
        const today = dayjs();

        let years = today.diff(start, 'year');
        if (years < 0) years = 0;

        const afterYears = start.add(years, 'year');
        let months = today.diff(afterYears, 'month');
        if (months < 0) months = 0;

        const afterMonths = afterYears.add(months, 'month');
        let days = today.diff(afterMonths, 'day');
        if (days < 0) days = 0;

        let antiguedad = 'P';
        if (years > 0) antiguedad += `${years}Y`;
        if (months > 0) antiguedad += `${months}M`;
        antiguedad += `${days}D`;

        payload.antiguedad = antiguedad; // Ejemplo: P3Y7D
      } else {
        payload.antiguedad = undefined;
      }

      if (editingEmployee) {
        await employeesService.updateEmployee(editingEmployee.id, payload);
        message.success('Empleado actualizado correctamente');
      } else {
        await employeesService.createEmployee(payload);
        message.success('Empleado creado correctamente');
      }
      setModalVisible(false);
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al guardar empleado');
    }
  };

  const handleDelete = async (id) => {
    try {
      await employeesService.deleteEmployee(id);
      message.success('Empleado eliminado correctamente');
      fetchEmployees();
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar empleado');
    }
  };

  const handleSearch = (value) => {
    fetchEmployees(value);
  };

  const columns = [
    {
      title: 'RFC',
      dataIndex: 'tax_id',
      key: 'tax_id',
      width: 140,
      render: (rfc) => <Tag color="blue">{rfc}</Tag>,
    },
    {
      title: 'Nombre',
      dataIndex: 'tax_name',
      key: 'tax_name',
    },
    {
      title: 'CURP',
      dataIndex: 'curp',
      key: 'curp',
      width: 140,
    },
    {
      title: 'NSS',
      dataIndex: 'nss',
      key: 'nss',
      width: 120,
    },
    {
      title: 'No. Empleado',
      dataIndex: 'num_empleado',
      key: 'num_empleado',
      width: 120,
    },
    {
      title: 'Periodicidad',
      dataIndex: 'periodicidad_pago',
      key: 'periodicidad_pago',
      width: 140,
    },
    {
      title: 'SDI',
      dataIndex: 'salario_diario_integrado',
      key: 'salario_diario_integrado',
      width: 140,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            size="small"
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar empleado?"
            okText="Sí"
            cancelText="No"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
              size="small"
            >
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(col => !(isReadOnly && col.key === 'actions'));

  return (
    <div style={{ padding: screens.xs ? '16px' : '24px' }}>
      <Card>
        <div style={{ 
          display: 'flex', 
          flexDirection: screens.md ? 'row' : 'column',
          justifyContent: 'space-between', 
          alignItems: screens.md ? 'center' : 'flex-start',
          gap: '16px',
          marginBottom: '24px' 
        }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <TeamOutlined style={{ marginRight: '8px' }} />
              Empleados
            </Title>
            <Text type="secondary">
              Gestiona tu catálogo de empleados
            </Text>
          </div>
          {!isReadOnly && <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            block={!screens.md}
          >
            Nuevo Empleado
          </Button>}
        </div>

        <Divider />

        <Row style={{ marginBottom: '16px' }}>
          <Col xs={24} md={12} lg={8}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder="Buscar empleados..."
                allowClear
                onChange={(e) => {
                  if (!e.target.value) handleSearch('');
                  // Note: Employees page doesn't use searchText state yet, 
                  // but we should trigger search on clear.
                  // For consistent behavior with other pages, we'll just use handleSearch directly here
                  handleSearch(e.target.value);
                }}
                onPressEnter={(e) => handleSearch(e.target.value)}
                style={{ borderRadius: '8px' }}
              />
              <Button 
                type="primary" 
                icon={<SearchOutlined />} 
                // Since there is no searchText state in this component yet, 
                // we can't easily implement the click handler without adding state.
                // However, the input onChange and onPressEnter will cover most use cases.
                // To fully support the button click, we would need to refactor to add searchText state.
                // For now, let's keep it consistent visually.
                style={{ borderRadius: '8px' }}
              />
            </div>
          </Col>
        </Row>

        <div ref={topPaginationRef} />
        <ResponsiveTable
          columns={columns}
          dataSource={employees}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} empleados`,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editingEmployee ? 'Editar Empleado' : 'Nuevo Empleado'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="company_id"
                label="Empresa"
                rules={[{ required: true, message: 'Seleccione una empresa' }]}
              >
                <Select placeholder="Seleccione empresa">
                  {companies.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.tax_name} - {c.tax_id}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="tax_id"
                label="RFC"
                rules={[
                  { required: true, message: 'RFC requerido' },
                  { pattern: /^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/, message: 'Formato RFC inválido' },
                ]}
              >
                <Input placeholder="Ej: ABC123456XYZ" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="tax_name"
                label="Nombre/Razón Social"
                rules={[{ required: true, message: 'Nombre requerido' }]}
              >
                <Input placeholder="Nombre del empleado" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="tax_location"
                label="Código Postal"
                rules={[
                  { required: true, message: 'Código postal requerido' },
                  { pattern: /^\d{5}$/, message: 'Ingresa un CP de 5 dígitos' },
                ]}
              >
                <Input placeholder="Ej: 44100" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="fiscal_regimen"
                label="Régimen Fiscal"
                rules={[{ required: true, message: 'Régimen fiscal requerido' }]}
              >
                <Select placeholder="Seleccione régimen fiscal">
                  <Option value="605">605 - Sueldos e Salarios</Option>
                  <Option value="606">606 - Arrendamiento</Option>
                  <Option value="608">608 - Sin obligaciones fiscales</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="curp"
                label="CURP"
              >
                <Input placeholder="CURP" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Datos Nómina</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="nss" label="NSS">
                <Input placeholder="Número de Seguridad Social" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="fecha_inicio_rel_laboral" label="Inicio Rel. Laboral">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="tipo_contrato" label="Tipo Contrato">
                <Select placeholder="Tipo de contrato">
                  <Option value="01">01 - Contrato de trabajo</Option>
                  <Option value="02">02 - Contrato por obra</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="tipo_regimen" label="Tipo Régimen">
                <Select placeholder="Tipo régimen">
                  <Option value="02">02 - Sueldos</Option>
                  <Option value="03">03 - Asimilados a salarios</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="num_empleado" label="Número Empleado">
                <Input placeholder="ID interno del empleado" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="departamento" label="Departamento">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="puesto" label="Puesto">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="riesgo_puesto" label="Riesgo Puesto">
                <Select placeholder="Riesgo">
                  <Option value="1">Clase I</Option>
                  <Option value="2">Clase II</Option>
                  <Option value="3">Clase III</Option>
                  <Option value="4">Clase IV</Option>
                  <Option value="5">Clase V</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="periodicidad_pago" label="Periodicidad Pago">
                <Select placeholder="Periodicidad">
                  <Option value="01">01 - Diario</Option>
                  <Option value="02">02 - Semanal</Option>
                  <Option value="03">03 - Catorcenal</Option>
                  <Option value="04">04 - Quincenal</Option>
                  <Option value="05">05 - Mensual</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="salario_base_cot_apor" label="Salario Base Cot. Apor">
                <Input placeholder="Ej: 350.00" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="salario_diario_integrado" label="Salario Diario Integrado">
                <Input placeholder="Ej: 420.00" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item name="clave_ent_fed" label="Clave Entidad Fed.">
                <Input placeholder="Ej: JAL" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button onClick={handleCancel}>Cancelar</Button>
              <Button type="primary" htmlType="submit">
                {editingEmployee ? 'Guardar Cambios' : 'Crear Empleado'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserEmployees;
